import { LOCALE_STORAGE_KEY } from '../shared/i18n/constants.js'

/** The install-shell strings. The brand title is not here — it is not localised. */
export interface InstallShellCopy {
  /** The primary install call-to-action button label. */
  install: string
  /** The note shown once the app is already installed. */
  installed: string
  /** The share-menu item name to emphasise in the iOS hint (rendered bold). */
  iosHintAction: string
  /** Text after the bolded share-menu item name (typically the trailing period). */
  iosHintAfter: string
  /** Text before the bolded share-menu item name in the iOS manual-install hint. */
  iosHintBefore: string
  /** The one-line lead explaining why to install. */
  lead: string
}

/**
 * PT-5645: localised copy for the install shell, kept off the lingui/Panda runtime on purpose.
 *
 * `InstallShell` is precached by the service worker and must keep booting when the delivery origin
 * is DNS-blocked, so it deliberately avoids the heavyweight i18n runtime the rest of the app uses
 * (lingui core + lazily-imported `.po` catalogs). This module is its featherweight stand-in: a
 * plain string map plus a dependency-free locale probe. Scope is en + vi — the app targets Vietnam,
 * so the shell offers Vietnamese and falls back to English for everyone else (the fully-localised
 * login/signup surfaces downstream still cover the other languages via lingui).
 */
export type InstallShellLocale = 'en' | 'vi'

const COPY: Record<InstallShellLocale, InstallShellCopy> = {
  en: {
    install: 'Install app',
    installed: 'Installed — open it from your home screen.',
    iosHintAction: 'Add to Home Screen',
    iosHintAfter: '.',
    iosHintBefore: 'On iPhone, tap the Share button and choose ',
    lead: 'Add the app to your home screen for uninterrupted access.',
  },
  vi: {
    install: 'Cài đặt ứng dụng',
    installed: 'Đã cài đặt — mở ứng dụng từ màn hình chính.',
    iosHintAction: 'Thêm vào Màn hình chính',
    iosHintAfter: '.',
    iosHintBefore: 'Trên iPhone, nhấn nút Chia sẻ và chọn ',
    lead: 'Thêm ứng dụng vào màn hình chính để truy cập không bị gián đoạn.',
  },
}

/** True for a Vietnamese BCP-47 tag (`vi`, `vi-VN`, …), case-insensitively. */
const isVietnameseTag = (tag: null | string | undefined): boolean =>
  typeof tag === 'string' && tag.toLowerCase().startsWith('vi')

/**
 * Resolve the shell locale from the raw signals. Pure (no globals) so the precedence is unit
 * testable: an explicit stored preference wins outright — a visitor who chose a non-Vietnamese
 * language is shown English rather than being flipped by a Vietnamese device locale — and only when
 * there is no stored choice does the device language decide.
 */
export const resolveInstallShellLocale = (
  storedLocale: null | string,
  languages: readonly string[],
): InstallShellLocale => {
  if (storedLocale != null && storedLocale !== '') {
    return isVietnameseTag(storedLocale) ? 'vi' : 'en'
  }
  return languages.some(isVietnameseTag) ? 'vi' : 'en'
}

/** Read the persisted locale preference, never throwing (sandboxed WebViews can raise on access). */
const readStoredLocale = (): null | string => {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

/** Read the device's ordered language preferences, never throwing. */
const readNavigatorLanguages = (): readonly string[] => {
  try {
    if (navigator.languages?.length) return navigator.languages
    return navigator.language ? [navigator.language] : []
  } catch {
    return []
  }
}

/** Detect the shell locale from the live browser globals, guarded so it always returns a locale. */
export const detectInstallShellLocale = (): InstallShellLocale =>
  resolveInstallShellLocale(readStoredLocale(), readNavigatorLanguages())

/** The install-shell copy for a locale (detected from the browser when not given). */
export const resolveInstallShellCopy = (locale: InstallShellLocale = detectInstallShellLocale()): InstallShellCopy =>
  COPY[locale]

/**
 * The dependency-free error/fallback screen strings (PT-5679). `AuthLoadError` and
 * `HandoffUnavailable` (app.tsx) render in exactly the situation the install shell does — the lazy
 * chunk carrying the lingui runtime failed, or nothing but the precached shell has loaded — so they
 * cannot use i18n either. They reuse this module's featherweight locale probe. The brand title is
 * not localised, mirroring {@link InstallShellCopy}.
 */
export interface FallbackScreenCopy {
  /** Lead shown when the auth chunk could not be loaded even after an auto-reload. */
  authLoadErrorLead: string
  /** Lead shown when the `/app` handoff loop has exhausted its bounce budget. */
  handoffUnavailableLead: string
  /** The reload button on the auth-load-error screen. */
  reload: string
  /** The retry button on the handoff-unavailable screen. */
  tryAgain: string
}

const FALLBACK_COPY: Record<InstallShellLocale, FallbackScreenCopy> = {
  en: {
    authLoadErrorLead: 'Something went wrong loading the app.',
    handoffUnavailableLead: 'We couldn’t reach your dashboard. Check your connection and try again.',
    reload: 'Reload',
    tryAgain: 'Try again',
  },
  vi: {
    authLoadErrorLead: 'Đã xảy ra lỗi khi tải ứng dụng.',
    handoffUnavailableLead: 'Không thể kết nối tới bảng điều khiển của bạn. Vui lòng kiểm tra kết nối và thử lại.',
    reload: 'Tải lại',
    tryAgain: 'Thử lại',
  },
}

/** The error/fallback screen copy for a locale (detected from the browser when not given). */
export const resolveFallbackScreenCopy = (
  locale: InstallShellLocale = detectInstallShellLocale(),
): FallbackScreenCopy => FALLBACK_COPY[locale]
